import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  HomeIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  PhoneIcon,
  CheckCircleIcon,
  ClockIcon,
  GiftIcon,
  UserGroupIcon,
  MapPinIcon,
} from '@heroicons/react/24/solid';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';

const ProductsPage = () => {
  const [activeTab, setActiveTab] = useState('personal');

  const products = {
    personal: [
      {
        name: 'Free Checking',
        description: 'Everyday checking with no monthly fees and no minimum balance.',
        icon: BanknotesIcon,
        features: ['No monthly maintenance fees', 'Free ATM access nationwide', 'Mobile check deposit', 'Online & mobile banking'],
        rate: 'No minimum balance',
        popular: true,
      },
      {
        name: 'High-Yield Savings',
        description: 'Competitive rates so your savings grow faster.',
        icon: BuildingLibraryIcon,
        features: ['4.25% APY on qualifying balances', 'No monthly fees', 'Automatic savings tools', 'Goal-based savings'],
        rate: '4.25% APY',
      },
      {
        name: 'Rewards Credit Card',
        description: 'Earn cash back on every purchase.',
        icon: CreditCardIcon,
        features: ['2% unlimited cash back', 'No annual fee', 'Mobile wallet compatible', 'Fraud protection'],
        rate: '0% APR intro 15 months',
      },
      {
        name: 'Home Loans',
        description: 'Mortgages and refinancing with local, member-focused service.',
        icon: HomeIcon,
        features: ['Fixed & adjustable rates', 'First-time buyer programs', 'Low down payment options', 'Digital application'],
        rate: 'From 6.49% APR',
      },
      {
        name: 'Personal Loans',
        description: 'Flexible financing for life’s big moments.',
        icon: BanknotesIcon,
        features: ['Quick approval', 'Competitive rates', 'No prepayment penalties', 'Fixed monthly payments'],
        rate: 'From 9.99% APR',
      },
      {
        name: 'Auto Loans',
        description: 'Drive away with a rate that works for you.',
        icon: BanknotesIcon,
        features: ['New and used vehicles', 'Competitive rates', 'Fast decisions', 'Optional GAP coverage'],
        rate: 'From 6.49% APR',
      },
    ],
    business: [
      {
        name: 'Business Checking',
        description: 'Accounts and tools built for small business.',
        icon: BanknotesIcon,
        features: ['Free transactions included', 'Online & mobile banking', 'Business debit cards', 'Payroll support'],
        rate: 'First year free',
        popular: true,
      },
      {
        name: 'Business Savings',
        description: 'Put your business cash to work.',
        icon: BuildingLibraryIcon,
        features: ['High-yield business savings', 'Money market options', 'Sweep accounts', 'No monthly fee options'],
        rate: 'Up to 2.50% APY',
      },
      {
        name: 'Business Credit Card',
        description: 'Simplify expenses and earn rewards.',
        icon: CreditCardIcon,
        features: ['2% cash back on purchases', 'Employee cards', 'Expense tools', 'No annual fee'],
        rate: 'No annual fee',
      },
      {
        name: 'Business Loans',
        description: 'Financing to grow your business.',
        icon: BanknotesIcon,
        features: ['Term loans & lines of credit', 'Equipment financing', 'SBA options', 'Commercial real estate'],
        rate: 'From 4.99% APR',
      },
      {
        name: 'Merchant Services',
        description: 'Accept payments wherever you do business.',
        icon: CreditCardIcon,
        features: ['Competitive processing rates', 'Next-day deposits', 'Mobile payments', 'Point-of-sale options'],
        rate: 'Custom pricing',
      },
      {
        name: 'Treasury & Cash Management',
        description: 'Optimize cash flow and control.',
        icon: PresentationChartLineIcon,
        features: ['Cash flow forecasting', 'Fraud prevention', 'Payment automation', 'Reporting'],
        rate: 'Custom solutions',
      },
    ],
  };

  const benefits = [
    { icon: ShieldCheckIcon, title: 'Secure & protected', description: 'NCUA insured with advanced security' },
    { icon: ClockIcon, title: '24/7 access', description: 'Bank anytime with online and mobile banking' },
    { icon: GiftIcon, title: 'Member rewards', description: 'Exclusive benefits for members' },
    { icon: UserGroupIcon, title: 'Personal service', description: 'Local team when you need guidance' },
  ];

  const helpOptions = [
    { icon: PhoneIcon, title: 'Call us', description: 'Speak with a product specialist', to: '/contact' },
    { icon: MapPinIcon, title: 'Visit us', description: '600 Kapiolani Blvd, Honolulu', to: '/contact' },
    { icon: ArrowRightIcon, title: 'Open an account', description: 'Get started in minutes', to: '/signup' },
  ];

  const currentProducts = products[activeTab];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-24 pb-16 lg:pt-28 lg:pb-20 bg-[#0a1628] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary-400 text-sm font-semibold tracking-widest uppercase mb-4">Products & services</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Financial solutions for
            <br />
            <span className="text-primary-300">every stage of life</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-10">
            Whether you’re managing day-to-day finances or growing a business, we have accounts and products designed for you.
          </p>

          <div className="inline-flex p-1.5 bg-white/10 rounded-full border border-white/20">
            <button
              onClick={() => setActiveTab('personal')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeTab === 'personal' ? 'bg-white text-primary-600' : 'text-white hover:bg-white/10'
              }`}
            >
              Personal
            </button>
            <button
              onClick={() => setActiveTab('business')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeTab === 'business' ? 'bg-white text-primary-600' : 'text-white hover:bg-white/10'
              }`}
            >
              Business
            </button>
          </div>
        </div>
      </section>

      {/* Benefits bar */}
      <section className="bg-slate-50 border-y border-slate-200/80 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((item) => (
              <div key={item.title} className="flex items-start gap-4">
                <div className="p-2 bg-primary-100 rounded-lg shrink-0">
                  <item.icon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-slate-500 text-sm mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {activeTab === 'personal' ? 'Personal banking' : 'Business banking'}
            </h2>
            <p className="text-slate-600 text-lg">
              {activeTab === 'personal'
                ? 'Accounts and loans designed for you and your family.'
                : 'Accounts and services to help your business grow.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentProducts.map((product, index) => (
              <div
                key={index}
                className="group relative flex flex-col bg-slate-50 rounded-2xl border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                {product.popular && (
                  <div className="absolute top-4 right-4 bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Popular
                  </div>
                )}
                <div className="p-8 flex-1 flex flex-col">
                  <div className="p-3 bg-primary-100 rounded-xl w-fit mb-5">
                    <product.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{product.name}</h3>
                  <p className="text-slate-600 text-sm mb-4 flex-1">{product.description}</p>
                  {product.rate && (
                    <span className="inline-block text-sm font-semibold text-primary-600 mb-5">{product.rate}</span>
                  )}
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700 text-sm">
                        <CheckCircleIcon className="h-4 w-4 text-primary-500 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="inline-flex items-center text-primary-600 font-semibold text-sm hover:text-primary-700 mt-auto"
                  >
                    Learn more
                    <ArrowRightIcon className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Need help */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Need help choosing?</h2>
            <p className="text-slate-600 text-lg">We’re here to help you find the right products.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {helpOptions.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="flex flex-col items-center text-center p-8 bg-white rounded-2xl border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="p-3 bg-primary-100 rounded-xl mb-4">
                  <item.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#0a1628]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">Ready to get started?</h2>
          <p className="text-slate-300 text-lg mb-10">
            Join Willowvale Credit Union and get access to these products plus member-only rates and service.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
            >
              Open an account
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-lg border border-white/20 transition-colors"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
